import { StateService } from '@infra/state/state.service';
import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import { Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { type Event, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { environment } from '@environments/environment';
import { NgbDropdownConfig, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { GLOBAL_LOADER_DISABLED_HEADER } from '@infra/http-interceptors/global-loader-headers';
import { CompanyDateFormatService } from '@common/services/company-date-format.service';
import { AuthTokenProviderService } from '@infra/auth/auth-token-provider.service';
import { NgHttpLoaderComponent, Spinkit } from 'ng-http-loader';
import { PrimeNG } from 'primeng/config';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, NgHttpLoaderComponent],
})
export class AppComponent implements OnInit {
  private readonly authTokenProviderService = inject(AuthTokenProviderService);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly stateService = inject(StateService);
  private readonly modalConfig = inject(NgbModalConfig);
  private readonly dropdownConfig = inject(NgbDropdownConfig);
  private readonly primeNg = inject(PrimeNG);
  private readonly companyDateFormatService = inject(CompanyDateFormatService);
  private readonly destroyRef = inject(DestroyRef);

  spinkit = Spinkit;
  globalLoaderDisabledHeader = GLOBAL_LOADER_DISABLED_HEADER;
  environment = environment;
  currentUrl: string;

  ngOnInit(): void {
    registerLocaleData(localeNL, 'nl-NL');

    let title = 'Reedge';
    if (environment.type !== 'production') {
      title = title + ' (' + environment.name + ')';
    }
    this.titleService.setTitle(title);

    window.addEventListener('storage', event => {
      if (event.storageArea === localStorage) {
        const validToken = this.authTokenProviderService.get();
        const isWelcomeScreen = this.currentUrl && this.currentUrl.includes('welcome');

        if (!validToken && !isWelcomeScreen) {
          this.router.navigate(['/login']);
        }
      }
    });

    // Workaround to make scroll prevent work in iOS Safari > 10.
    try {
      window.addEventListener(
        'touchmove',
        () => {
          // Do nothing.
        },
        { passive: false }
      );
    } catch {
      // Ignore.
    }

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.googleAnalyticsService.pageView(event.url, event.url);
        this.stateService.showSearch(true);
      } else if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
        this.onActivate();
      }
    });

    // PrimeNG date pickers, such as the date column filter, show and parse dates in the company date format.
    this.stateService.currentCompany$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.primeNg.setTranslation({ dateFormat: this.companyDateFormatService.primeNgDateFormat });
    });

    this.modalConfig.keyboard = false;
    this.modalConfig.backdrop = 'static';

    // Space dropdown menus with their CSS margin, as Bootstrap 4 did. ng-bootstrap's Popper 2 resets the margin
    // and adds a 2px offset instead, which puts a gap under menus that are meant to sit against their toggle.
    this.dropdownConfig.popperOptions = options => ({
      ...options,
      modifiers: [
        ...(options.modifiers ?? []),
        { name: 'offset', options: { offset: [0, 0] } },
        {
          name: 'keepMenuMargin',
          enabled: true,
          phase: 'afterWrite',
          fn: ({ state }) => {
            state.elements.popper.style.removeProperty('margin');
          },
        },
      ],
    });
  }

  onActivate(): void {
    const scrollToTop = window.setInterval(() => {
      const scrollY = window.scrollY;
      if (scrollY > 0) {
        window.scrollTo(0, scrollY - 20); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16);
  }
}
